import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  CButton,
  CCard,
  CCardBody,
  CCol,
  CImage,
  CRow,
  CSpinner,
  CBadge,
  CNav,
  CNavItem,
  CNavLink,
  CTabContent,
  CTabPane,
  CFormTextarea,
} from "@coreui/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFileInvoiceDollar,
  faClockRotateLeft,
  faShieldHalved,
  faDownload,
  faClose,
} from "@fortawesome/free-solid-svg-icons";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";
import { Formik } from "formik";
import { toast } from "react-toastify";
import { useTranslationContext } from "@/context/TranslationContext";
import { subscribedUser } from "@/constants/translation/subscribedUser.translation";
import { subscriptionPlan } from "@/constants/translation/subscriptionPlan.translation";
import { breadcrumb_key } from "@/constants/translation/common.translation";
import { masters } from "@/libs/routes";
import {
  useSubscribedUser,
  useSubscribedUserTransactionList,
  useSubscribedUserDocuments,
  useApproveVerification,
  useRejectVerification,
} from "@/hooks/useSubscribedUser";
import { setFormatDate, dateTimeFormat } from "@/utils/dateFormate";
import { TNTable } from "@/components";
import avatar from "@/assets/images/avatars/DefaultImg.png";
import constants from "@/libs/constant";

// API responses pass every value through the app's shared encryption round-trip, which
// stringifies booleans (true -> "true", false -> "false") — same convention as isTrue in
// src/pages/user/freelancer/[freelancer_user_id]/view/index.js.
const isAutoRenewTrue = (value) => value === true || value === constants.status.apiStatus.true;

const TABS = {
  SUBSCRIPTION_INFO: "subscription_info",
  TRANSACTION_HISTORY: "transaction_history",
  VERIFICATION: "verification",
};

const getVerificationStatusBadgeClass = (status) => {
  switch (status) {
    case "approved":
      return "success";
    case "pending":
      return "warning";
    case "rejected":
      return "danger";
    default:
      return "secondary";
  }
};

const getSubscriptionStatusBadgeClass = (status) => {
  switch (status) {
    case "active":
      return "success";
    case "cancelled":
      return "danger";
    case "expired":
      return "warning";
    default:
      return "secondary";
  }
};

// Same mapping as getUserTypeBadgeClass in src/pages/masters/subscription-plans/list/index.js
// (not exported there, so replicated locally here) — used for the Plan Type badge.
const getUserTypeBadgeClass = (userType) => {
  switch (userType) {
    case constants.userType.freelancerUser:
      return "primary";
    case constants.userType.normalUser:
      return "secondary";
    default:
      return "secondary";
  }
};

// status_label from the transactions endpoint is a ready-to-display string
// (e.g. "Successful"/"Failed"/"Pending"/"Admin Grant"/"Referral Reward", or - for a verified
// row - the underlying lifecycle event instead: "Purchased"/"Renewed"/"Cancelled"/"Expired"/
// "Recovered"/"Restarted"/"Revoked"/"Renewal Status Changed"; see EVENT_TYPE_LABELS in
// constants/subscribed-user.constant.js on the backend).
const getTransactionStatusBadgeClass = (statusLabel) => {
  switch (statusLabel) {
    case "Successful":
    case "Purchased":
    case "Renewed":
    case "Recovered":
    case "Restarted":
      return "success";
    case "Failed":
    case "Cancelled":
    case "Expired":
    case "Revoked":
      return "danger";
    case "Pending":
    case "Renewal Status Changed":
      return "warning";
    case "Admin Grant":
    case "Referral Reward":
      return "info";
    default:
      return "secondary";
  }
};

const ViewSubscribedUser = () => {
  const t = useTranslationContext();
  const navigate = useNavigate();
  const { id } = useParams();

  const [activeTab, setActiveTab] = useState(TABS.SUBSCRIPTION_INFO);
  const [transactionPage, setTransactionPage] = useState(constants.pagination.defaultPage);

  const {
    data: response,
    isLoading,
    error: subscribedUserError,
  } = useSubscribedUser(id, null, (error) => {
    console.error("Error fetching subscribed user details:", error);
  });

  const subscribedUserData = response?.data;

  const { data: transactionResponse, isLoading: isTransactionLoading } = useSubscribedUserTransactionList(
    id,
    {
      page: transactionPage,
      limit: constants.pagination.defaultPageSize,
    },
    { enabled: activeTab === TABS.TRANSACTION_HISTORY },
  );

  const { data: documentsResponse, isLoading: isDocumentsLoading } = useSubscribedUserDocuments(id, {
    enabled: activeTab === TABS.VERIFICATION,
  });

  const { mutate: doApproveVerification } = useApproveVerification(() => {
    toast.success(t(subscribedUser.view.verification.messages.approve_success));
  });

  const { mutate: doRejectVerification } = useRejectVerification(() => {
    toast.success(t(subscribedUser.view.verification.messages.reject_success));
  });

  if (isLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "400px" }}>
        <CSpinner color="primary" />
      </div>
    );
  }

  if (subscribedUserError || !subscribedUserData) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "400px" }}>
        <div className="text-center">
          <h4>{t(subscribedUser.view.error.not_found)}</h4>
          <p>{t(subscribedUser.view.error.not_found_description)}</p>
          <CButton color="primary" onClick={() => navigate(masters.subscribedUsers.list)}>
            {t(subscribedUser.view.back_to_list)}
          </CButton>
        </div>
      </div>
    );
  }

  const na = t(subscribedUser.view.defaults.na);
  const user = subscribedUserData.user || {};
  const subscriptionInfo = subscribedUserData.subscription_info || {};
  const isAutoRenew = isAutoRenewTrue(subscriptionInfo.auto_renew);

  const transactions = transactionResponse?.data?.transactions || [];
  const transactionPagination = transactionResponse?.data?.pagination;

  const verificationData = documentsResponse?.data;
  const verificationDocuments = verificationData?.documents || [];
  const verificationStatus = verificationData?.verification_status;

  const handleApproveVerification = () => {
    confirmAlert({
      customUI: ({ onClose }) => (
        <div className="alert-box">
          <div className="alert-popup">
            <FontAwesomeIcon className="alert-close" icon={faClose} onClick={onClose} />
            <h2 className="alert-title">{t(subscribedUser.view.verification.approve_popup.heading)}</h2>
            <p className="text-center mb-3">{t(subscribedUser.view.verification.approve_popup.message)}</p>
            <div className="d-flex gap-2 justify-content-center flex-nowrap">
              <CButton
                color="primary"
                className="table-delete-button"
                onClick={() => {
                  doApproveVerification(id);
                  onClose();
                }}
              >
                {t(subscribedUser.view.verification.approve_popup.yes_btn_text)}
              </CButton>
              <CButton className="table-primary-button" color="danger" onClick={onClose}>
                {t(subscribedUser.view.verification.approve_popup.no_btn_text)}
              </CButton>
            </div>
          </div>
        </div>
      ),
    });
  };

  const handleRejectVerification = () => {
    const popup = subscribedUser.view.verification.reject_popup;

    confirmAlert({
      customUI: ({ onClose }) => (
        <Formik
          initialValues={{ reason: "" }}
          validate={(values) => {
            const errors = {};
            if (!values.reason || !values.reason.trim()) {
              errors.reason = t(popup.reason_required);
            }
            return errors;
          }}
          onSubmit={(values, { setSubmitting }) => {
            doRejectVerification({ uuid: id, reason: values.reason.trim() });
            setSubmitting(false);
            onClose();
          }}
        >
          {({ values, errors, touched, handleChange, handleBlur, handleSubmit, isSubmitting }) => (
            <div className="alert-box admin-status-popup">
              <div className="alert-popup">
                <FontAwesomeIcon className="alert-close" icon={faClose} onClick={onClose} />
                <h2 className="alert-title">{t(popup.heading)}</h2>
                <p className="text-center mb-3">{t(popup.message)}</p>
                <div className="mb-3 text-start">
                  <CFormTextarea
                    id="reason"
                    name="reason"
                    rows={3}
                    placeholder={t(popup.reason_placeholder)}
                    value={values.reason}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    invalid={touched.reason && !!errors.reason}
                  />
                  {touched.reason && errors.reason && <div className="text-danger small mt-1">{errors.reason}</div>}
                </div>
                <div className="d-flex gap-2 justify-content-center flex-nowrap">
                  <CButton
                    color="primary"
                    className="table-delete-button"
                    disabled={isSubmitting}
                    onClick={handleSubmit}
                  >
                    {t(popup.yes_btn_text)}
                  </CButton>
                  <CButton className="table-primary-button" color="danger" onClick={onClose} disabled={isSubmitting}>
                    {t(popup.no_btn_text)}
                  </CButton>
                </div>
              </div>
            </div>
          )}
        </Formik>
      ),
    });
  };

  const transactionColumns = [
    {
      id: "plan_name",
      accessorKey: "plan_name",
      header: t(subscribedUser.view.transaction_history.table.plan_name_col_heading),
      cell: ({ getValue }) => <span>{getValue() || na}</span>,
    },
    {
      id: "date_time",
      accessorKey: "date_time",
      header: t(subscribedUser.view.transaction_history.table.date_time_col_heading),
      cell: ({ getValue }) => <span>{getValue() ? dateTimeFormat(getValue()) : na}</span>,
    },
    {
      id: "status_label",
      accessorKey: "status_label",
      header: t(subscribedUser.view.transaction_history.table.status_col_heading),
      cell: ({ getValue }) => {
        const value = getValue();
        return <CBadge color={getTransactionStatusBadgeClass(value)}>{value || na}</CBadge>;
      },
    },
    {
      id: "amount",
      accessorKey: "amount",
      header: t(subscribedUser.view.transaction_history.table.amount_col_heading),
      cell: ({ getValue }) => {
        const value = getValue();
        return <span>{value != null ? `$${value}` : na}</span>;
      },
    },
  ];

  return (
    <div className="view-subscribed-user-container">
      <CCard className="mb-4 profile-header-card">
        <CCardBody>
          <CRow className="align-items-center">
            <CCol md="auto" className="text-center mb-3 mb-md-0">
              <CImage
                src={user.profile_photo_url || avatar}
                alt="profile_img"
                className="rounded-circle"
                style={{ width: "120px", height: "120px", objectFit: "cover", border: "4px solid #212631" }}
              />
            </CCol>
            <CCol>
              <h2 className="mb-1">{user.full_name || na}</h2>
              <div className="text-muted mb-1">{user.email || na}</div>
              <div className="text-muted mb-2">{user.phone || na}</div>
              <div className="d-flex flex-wrap gap-2 mb-2">
                <CBadge color={getSubscriptionStatusBadgeClass(subscribedUserData.subscription_status)}>
                  {t(subscribedUser.view.subscription_status[subscribedUserData.subscription_status]) ||
                    subscribedUserData.subscription_status}
                </CBadge>
                <CBadge color={getUserTypeBadgeClass(subscribedUserData.plan_type)}>
                  {t(subscriptionPlan.user_type[subscribedUserData.plan_type]) || subscribedUserData.plan_type}
                </CBadge>
              </div>
            </CCol>
          </CRow>
        </CCardBody>
      </CCard>

      <CNav variant="tabs" className="mb-4 view-user-tabs">
        {[
          [TABS.SUBSCRIPTION_INFO, faFileInvoiceDollar, subscribedUser.view.tabs.subscription_info],
          [TABS.TRANSACTION_HISTORY, faClockRotateLeft, subscribedUser.view.tabs.transaction_history],
          [TABS.VERIFICATION, faShieldHalved, subscribedUser.view.tabs.verification],
        ].map(([tabKey, icon, labelKey]) => (
          <CNavItem key={tabKey}>
            <CNavLink
              href="#"
              active={activeTab === tabKey}
              onClick={(e) => {
                e.preventDefault();
                setActiveTab(tabKey);
              }}
            >
              <FontAwesomeIcon icon={icon} className="me-2" />
              {t(labelKey)}
            </CNavLink>
          </CNavItem>
        ))}
      </CNav>

      <CTabContent>
        <CTabPane visible={activeTab === TABS.SUBSCRIPTION_INFO}>
          <CCard>
            <CCardBody>
              <h4 className="card-title mb-4">{t(subscribedUser.view.subscription_info.title)}</h4>
              <CRow>
                <CCol md={6} className="mb-3">
                  <strong>{t(subscribedUser.view.subscription_info.plan_name_label)}:</strong>
                  <p className="text-muted">{subscriptionInfo.plan_name || na}</p>
                </CCol>
                <CCol md={6} className="mb-3">
                  <strong>{t(subscribedUser.view.subscription_info.duration_label)}:</strong>
                  <p className="text-muted">
                    {subscriptionInfo.duration_value
                      ? `${subscriptionInfo.duration_value} ${subscriptionInfo.duration_unit || ""}`.trim()
                      : na}
                  </p>
                </CCol>
                <CCol md={6} className="mb-3">
                  <strong>{t(subscribedUser.view.subscription_info.amount_paid_label)}:</strong>
                  <p className="text-muted">
                    {subscriptionInfo.amount_paid != null ? `$${subscriptionInfo.amount_paid}` : na}
                  </p>
                </CCol>
                <CCol md={6} className="mb-3">
                  <strong>{t(subscribedUser.view.subscription_info.purchase_date_label)}:</strong>
                  <p className="text-muted">
                    {subscriptionInfo.purchase_date ? setFormatDate(subscriptionInfo.purchase_date) : na}
                  </p>
                </CCol>
                <CCol md={6} className="mb-3">
                  <strong>{t(subscribedUser.view.subscription_info.expiry_date_label)}:</strong>
                  <p className="text-muted">
                    {subscriptionInfo.expiry_date ? setFormatDate(subscriptionInfo.expiry_date) : na}
                  </p>
                </CCol>
                <CCol md={6} className="mb-3">
                  <strong>{t(subscribedUser.view.subscription_info.auto_renew_label)}:</strong>
                  <p className="text-muted">
                    <CBadge color={isAutoRenew ? "success" : "secondary"}>
                      {isAutoRenew ? t(subscribedUser.list.auto_renew.yes) : t(subscribedUser.list.auto_renew.no)}
                    </CBadge>
                  </p>
                </CCol>
                <CCol md={6} className="mb-3">
                  <strong>{t(subscribedUser.view.subscription_info.payment_method_label)}:</strong>
                  <p className="text-muted">
                    {subscriptionInfo.payment_method_label || subscriptionInfo.payment_method || na}
                  </p>
                </CCol>
              </CRow>
            </CCardBody>
          </CCard>
        </CTabPane>

        <CTabPane visible={activeTab === TABS.TRANSACTION_HISTORY}>
          <CCard>
            <CCardBody>
              <h4 className="card-title mb-4">{t(subscribedUser.view.transaction_history.title)}</h4>
              <TNTable
                columns={transactionColumns}
                data={transactions}
                paginationData={transactionPagination}
                onSelectPage={(pageIndex) => {
                  setTransactionPage(pageIndex + 1);
                }}
                pageIndexGet={transactionPage - 1}
                isLoading={isTransactionLoading}
                t={t}
              />
            </CCardBody>
          </CCard>
        </CTabPane>

        <CTabPane visible={activeTab === TABS.VERIFICATION}>
          <CCard>
            <CCardBody>
              <div className="d-flex flex-wrap align-items-center justify-content-between mb-4 gap-2">
                <h4 className="card-title mb-0">{t(subscribedUser.view.verification.title)}</h4>
                {!isDocumentsLoading && verificationDocuments.length > 0 && (
                  <div className="d-flex flex-wrap gap-2">
                    {verificationStatus !== "approved" && (
                      <CButton size="sm" color="success" onClick={handleApproveVerification}>
                        {t(subscribedUser.view.verification.actions.approve)}
                      </CButton>
                    )}
                    {verificationStatus !== "rejected" && (
                      <CButton size="sm" color="danger" onClick={handleRejectVerification}>
                        {t(subscribedUser.view.verification.actions.reject)}
                      </CButton>
                    )}
                  </div>
                )}
              </div>

              {isDocumentsLoading ? (
                <div className="d-flex justify-content-center py-4">
                  <CSpinner color="primary" />
                </div>
              ) : (
                <>
                  <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
                    <strong>{t(subscribedUser.view.verification.status_label)}:</strong>
                    <CBadge color={getVerificationStatusBadgeClass(verificationStatus)}>
                      {t(subscribedUser.view.verification.status[verificationStatus]) || verificationStatus || na}
                    </CBadge>
                  </div>
                  {verificationStatus === "rejected" && verificationData?.rejection_reason && (
                    <div className="text-danger small mb-3">
                      <strong>{t(subscribedUser.view.verification.rejection_reason_label)}:</strong>{" "}
                      {verificationData.rejection_reason}
                    </div>
                  )}

                  <hr className="my-3" />

                  {verificationDocuments.length ? (
                    <div className="d-flex flex-wrap gap-3">
                      {verificationDocuments.map((doc) => (
                        <a
                          key={doc.uuid}
                          href={doc.doc_url}
                          download={doc.doc_name || true}
                          target="_blank"
                          rel="noreferrer"
                          className="text-center text-decoration-none"
                          style={{ width: "150px" }}
                        >
                          <div
                            className="d-flex align-items-center justify-content-center border rounded"
                            style={{ width: "150px", height: "150px" }}
                          >
                            <FontAwesomeIcon icon={faDownload} size="2x" />
                          </div>
                          <div className="small text-muted mt-1">{doc.doc_name}</div>
                          {doc.type && (
                            <CBadge color="secondary" className="mt-1">
                              {t(subscribedUser.view.verification.document_type[doc.type]) || doc.type}
                            </CBadge>
                          )}
                        </a>
                      ))}
                    </div>
                  ) : (
                    <p className="text-muted">{t(subscribedUser.view.verification.no_documents)}</p>
                  )}
                </>
              )}
            </CCardBody>
          </CCard>
        </CTabPane>
      </CTabContent>
    </div>
  );
};

ViewSubscribedUser.breadCrumbArray = [
  {
    name: breadcrumb_key.masters?.subscribedUser?.list,
    pathname: masters.subscribedUsers.list,
    active: false,
  },
  {
    name: breadcrumb_key.masters?.subscribedUser?.view,
    pathname: "",
    active: true,
  },
];

export default ViewSubscribedUser;
